import { Paper, Title, Group, Select, Table, Text, Badge, Stack } from '@mantine/core';
import { IconTrophy } from '@tabler/icons-react';
import { useState } from 'react';
import type { DashboardMetrics } from '../hooks/useDashboardMetrics';

type Top5Type = 'category-count' | 'category-value' | 'location-count' | 'location-value' | 'person-count' | 'person-value';

interface Top5WidgetProps {
  metrics: DashboardMetrics;
}

const top5Options: { value: Top5Type; label: string }[] = [
  { value: 'category-count', label: 'Top Categories (by count)' },
  { value: 'category-value', label: 'Top Categories (by value)' },
  { value: 'location-count', label: 'Top Locations (by count)' },
  { value: 'location-value', label: 'Top Locations (by value)' },
  { value: 'person-count', label: 'Top People (by count)' },
  { value: 'person-value', label: 'Top People (by value)' },
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export function Top5Widget({ metrics }: Top5WidgetProps) {
  const [selectedType, setSelectedType] = useState<Top5Type>('category-count');

  const getData = () => {
    switch (selectedType) {
      case 'category-count':
        return metrics.assetsByCategory
          .slice(0, 5)
          .map((item, index) => ({
            rank: index + 1,
            name: item.name,
            primary: item.count,
            secondary: item.value,
            primaryLabel: 'assets',
          }));
      case 'category-value':
        return [...metrics.assetsByCategory]
          .sort((a, b) => b.value - a.value)
          .slice(0, 5)
          .map((item, index) => ({
            rank: index + 1,
            name: item.name,
            primary: item.value,
            secondary: item.count,
            primaryLabel: 'value',
          }));
      case 'location-count':
        return metrics.assetsBySite
          .slice(0, 5)
          .map((item, index) => ({
            rank: index + 1,
            name: item.name,
            primary: item.count,
            secondary: item.value,
            primaryLabel: 'assets',
          }));
      case 'location-value':
        return [...metrics.assetsBySite]
          .sort((a, b) => b.value - a.value)
          .slice(0, 5)
          .map((item, index) => ({
            rank: index + 1,
            name: item.name,
            primary: item.value,
            secondary: item.count,
            primaryLabel: 'value',
          }));
      case 'person-count':
        return metrics.assetsByPerson
          .slice(0, 5)
          .map((item, index) => ({
            rank: index + 1,
            name: item.name,
            primary: item.count,
            secondary: item.value,
            primaryLabel: 'assets',
          }));
      case 'person-value':
        return [...metrics.assetsByPerson]
          .sort((a, b) => b.value - a.value)
          .slice(0, 5)
          .map((item, index) => ({
            rank: index + 1,
            name: item.name,
            primary: item.value,
            secondary: item.count,
            primaryLabel: 'value',
          }));
      default:
        return [];
    }
  };

  const data = getData();
  const isValueType = selectedType.endsWith('-value');

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1: return 'yellow';
      case 2: return 'gray';
      case 3: return 'orange';
      default: return 'blue';
    }
  };

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconTrophy size={20} color="var(--mantine-color-yellow-6)" />
            <Title order={4}>Top 5</Title>
          </Group>
          <Select
            size="xs"
            data={top5Options}
            value={selectedType}
            onChange={(value) => setSelectedType((value as Top5Type) || 'category-count')}
            style={{ minWidth: 200 }}
          />
        </Group>

        {data.length === 0 ? (
          <Text c="dimmed" ta="center" py="lg">
            No data available
          </Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 50 }}>#</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>
                  {isValueType ? 'Value' : 'Count'}
                </Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>
                  {isValueType ? 'Count' : 'Value'}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((item) => (
                <Table.Tr key={`${item.rank}-${item.name}`}>
                  <Table.Td>
                    <Badge
                      size="sm"
                      variant="light"
                      color={getRankColor(item.rank)}
                      circle
                    >
                      {item.rank}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={item.rank <= 3 ? 500 : 400}>
                      {item.name}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="sm" fw={500}>
                      {isValueType ? formatCurrency(item.primary) : item.primary}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Text size="sm" c="dimmed">
                      {isValueType ? `${item.secondary} assets` : formatCurrency(item.secondary)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Paper>
  );
}
